/**
 * Sidebar AI-индикатор (фича 031, FR-001..FR-003).
 *
 * Компактный блок: имя модели (truncate), цветная точка статуса, две
 * цифры — входящие и исходящие токены (компактный формат для значений
 * ≥1000). Tooltip — точные числа + последняя активность.
 *
 * Поведение при ошибке загрузки или отсутствии данных — graceful: блок
 * показывает "—" и не ломает sidebar.
 */

import { useAiStatus } from "@/hooks/use-ai-status";
import type { AiStatusEnvelope } from "@efes/shared";

/** Компактный формат числа: 12345 → "12.3K", 1234567 → "1.2M". */
function compactNumber(n: number): string {
	if (n < 1000) return String(n);
	if (n < 1_000_000) return `${(n / 1000).toFixed(1)}K`;
	if (n < 1_000_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	return `${(n / 1_000_000_000).toFixed(1)}G`;
}

function statusDotClass(status: AiStatusEnvelope["status"]): string {
	if (status === "green") return "bg-green-500";
	if (status === "red") return "bg-red-500";
	return "bg-slate-300"; // no-data
}

function statusLabel(status: AiStatusEnvelope["status"]): string {
	if (status === "green") return "Подключение в порядке";
	if (status === "red") return "Нет ответа от AI";
	return "Нет данных";
}

export function AiUsageIndicator() {
	const { data, isError } = useAiStatus();

	if (isError || !data) {
		return (
			<div
				className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-500"
				title="AI-индикатор недоступен"
			>
				<div className="flex items-center gap-1.5">
					<span className="h-2 w-2 rounded-full bg-slate-300" aria-hidden="true" />
					<span className="truncate">—</span>
				</div>
				<div className="mt-0.5 font-mono text-slate-400">— / —</div>
			</div>
		);
	}

	const { modelName, status } = data;
	const promptTokens = data.aggregated?.promptTokens ?? 0;
	const completionTokens = data.aggregated?.completionTokens ?? 0;
	const sessionsCount = data.aggregated?.sessionsCount ?? 0;
	const lastCallAt = data.lastCallAt;

	const tooltipLines = [
		`Модель: ${modelName ?? "не определена"}`,
		`Статус: ${statusLabel(status)}`,
		`Входящие: ${promptTokens.toLocaleString("ru-RU")}`,
		`Исходящие: ${completionTokens.toLocaleString("ru-RU")}`,
		sessionsCount > 0 ? `Сессий в счёте: ${sessionsCount}` : null,
		lastCallAt ? `Последний вызов: ${new Date(lastCallAt).toLocaleString("ru-RU")}` : null,
	].filter(Boolean);

	return (
		<div
			className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-700"
			title={tooltipLines.join("\n")}
		>
			<div className="flex items-center gap-1.5">
				<span
					className={`h-2 w-2 shrink-0 rounded-full ${statusDotClass(status)}`}
					aria-hidden="true"
				/>
				<span className="truncate font-medium" title={modelName ?? undefined}>
					{modelName ?? "Модель не определена"}
				</span>
			</div>
			<div className="mt-0.5 flex items-center gap-2 font-mono text-slate-500">
				<span aria-label="Входящие токены">↑ {compactNumber(promptTokens)}</span>
				<span aria-label="Исходящие токены">↓ {compactNumber(completionTokens)}</span>
			</div>
		</div>
	);
}
